import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from '~/shadcn/ui/spinner'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { useAmbassadorDetail } from '~/@biz_ambassador/ambassador/useAmbassadorDetail'
import { useAmbassadorData } from '~/@biz_ambassador/ambassador/useAmbassadorData'
import { useAmbassadorExport } from '~/@biz_ambassador/export/useAmbassadorExport'
import { ExportDialog } from '~/@biz_ambassador/export/ExportDialog'
import { DetailHeader } from './components/DetailHeader'
import { DetailSummary } from './components/DetailSummary'
import { ClientsSection } from './components/ClientsSection'
import { AmbassadorStatusAction } from './components/AmbassadorStatusAction'
import { DeactivateDialog } from './components/DeactivateDialog'

const DASHBOARD_ROUTE = '/settings/ambassador'

const AmbassadorDetailPage = () => {
  const navigate = useNavigate()
  const { ambassadorId = '' } = useParams<{ ambassadorId: string }>()
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false)

  const detailQuery = useAmbassadorDetail(ambassadorId)
  const { setEnabledMutation } = useAmbassadorData()
  const { setNotification } = useLocalNotifications()
  const detail = detailQuery.data

  const { months, downloadingKey, handleExportSingle } = useAmbassadorExport(
    detail?.createdAt ?? null
  )

  const goToDashboard = () => navigate(DASHBOARD_ROUTE)

  const handleActivate = () =>
    setEnabledMutation.mutate(
      { ambassadorId, isEnabled: true },
      {
        onSuccess: () =>
          setNotification('success', 'Ambasádor bol aktivovaný.'),
        onError: (err) =>
          setNotification(
            'error',
            getApiErrorMessage(err, 'Ambasádora sa nepodarilo aktivovať.')
          ),
      }
    )

  return (
    <PageContainer className="flex flex-col shrink gap-10 w-full pt-10 pb-15 md:pl-40 md:pr-15">
      <DetailHeader
        onBack={goToDashboard}
        onExport={() => setIsExportDialogOpen(true)}
        exportDisabled={!detail}
      />

      {detailQuery.isLoading ? (
        <Spinner className="size-8" />
      ) : detail ? (
        <>
          <DetailSummary detail={detail} />
          <ClientsSection clients={detail.clients} isLoading={false} />
          <AmbassadorStatusAction
            isEnabled={detail.isEnabled}
            onActivate={handleActivate}
            onDeactivate={() => setIsDeactivateDialogOpen(true)}
          />
        </>
      ) : (
        <p className="text-sm font-medium text-muted">
          Ambasádora sa nepodarilo načítať.
        </p>
      )}

      {detail && (
        <ExportDialog
          open={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
          downloadingKey={downloadingKey}
          months={months}
          exportHandler={({ month, year }) =>
            handleExportSingle({ month, year }, ambassadorId)
          }
        />
      )}
      <DeactivateDialog
        open={isDeactivateDialogOpen}
        onOpenChange={setIsDeactivateDialogOpen}
        onConfirm={() =>
          setEnabledMutation.mutateAsync({ ambassadorId, isEnabled: false })
        }
      />
    </PageContainer>
  )
}

export default AmbassadorDetailPage
